// ═══════════════════════════════════════════════════════════════════
//  modules/bugs/BugsPage.js
//  BugsPage — Création · Prise en charge · Suivi
//
//  Trois colonnes, un formulaire, rien d'autre. Elle lit et écrit dans
//  la MÊME table que Gestion projet : un bug déclaré ici apparaît là-bas.
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from "react";
import { API_BASE } from "../../socle/api";
import { useIsMobile } from "../../socle/hooks";
import { THEME } from "../../socle/theme";

const COLONNES = [
  { cle: "a_faire",  titre: "Création",         aide: "Signalé. Personne ne s'en occupe encore.", couleur: THEME.amber,   fond: THEME.amberDim },
  { cle: "en_cours", titre: "Prise en charge",  aide: "Quelqu'un se l'est assigné.",              couleur: THEME.accent,  fond: THEME.accentDim },
  { cle: "termine",  titre: "Suivi",            aide: "Corrigé et vérifié.",                      couleur: THEME.emerald, fond: THEME.emeraldDim },
];

const PRIO = {
  haute:   { label: "haute",   couleur: THEME.red,       fond: THEME.redDim },
  moyenne: { label: "moyenne", couleur: THEME.amber,     fond: THEME.amberDim },
  basse:   { label: "basse",   couleur: THEME.textMuted, fond: THEME.surface },
};

const panneau = { background: THEME.panel, border: `1px solid ${THEME.border}`, borderRadius: 14, padding: 16 };
const champ = { background: THEME.surface, border: `1px solid ${THEME.border}`, color: THEME.text,
                borderRadius: 8, padding: "9px 12px", fontSize: 13, width: "100%",
                boxSizing: "border-box", outline: "none", fontFamily: "inherit" };
const bouton = (fond, bord, col) => ({ background: fond, border: `1px solid ${bord}`, color: col,
  borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit" });
const petit = (fond, bord, col) => ({ ...bouton(fond, bord, col), padding: "4px 9px", fontSize: 11.5 });
const etiquette = { color: THEME.textMuted, fontSize: 11, fontWeight: 700,
                    textTransform: "uppercase", letterSpacing: 0.5 };

const jour = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "—");

export function BugsPage({ token, user }) {
  const isMobile = useIsMobile();
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [ouvert, setOuvert] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [ouvre, setOuvre] = useState(null);    // id du bug déplié
  const [corbeille, setCorbeille] = useState(false);
  const [aConfirmer, setAConfirmer] = useState(null);   // id en attente de confirmation
  const vide = { titre: "", ou: "", quand: "", attendu: "", constate: "",
                 reproductible: "", joueur: "", priorite: "moyenne" };
  const [f, setF] = useState(vide);

  const h = { Authorization: `Bearer ${token}` };
  const hj = { ...h, "Content-Type": "application/json" };

  const charger = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/api/bugs${corbeille ? "?corbeille=1" : ""}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("Le serveur n'a pas répondu correctement.");
      setDonnees(await r.json());
      setErreur(null);
    } catch (e) {
      setErreur("Liste des bugs injoignable. " + e.message);
    }
  }, [token, corbeille]);

  useEffect(() => { setAConfirmer(null); charger(); }, [charger]);
  // Si la corbeille est refusée (rôle insuffisant, ou perdu en cours de
  // route), on revient à la liste plutôt que de laisser un écran vide avec
  // un message rouge et aucune sortie.
  useEffect(() => {
    if (corbeille && donnees && donnees.droits && !donnees.droits.admin) setCorbeille(false);
  }, [corbeille, donnees]);
  useEffect(() => {
    const id = setInterval(charger, 30000);
    return () => clearInterval(id);
  }, [charger]);

  const declarer = async () => {
    if (f.titre.trim().length < 5) { setErreur("Donne un titre un peu plus parlant."); return; }
    setEnvoi(true);
    try {
      const r = await fetch(`${API_BASE}/api/bugs`, {
        method: "POST", headers: hj, body: JSON.stringify(f) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Erreur serveur");
      setF(vide); setOuvert(false); setErreur(null);
      charger();
    } catch (e) { setErreur(e.message); }
    finally { setEnvoi(false); }
  };

  // On réutilise les routes du kanban : un bug EST une tâche.
  const bouger = async (id, status) => {
    await fetch(`${API_BASE}/api/project/tasks/${id}/status`, {
      method: "PATCH", headers: hj, body: JSON.stringify({ status }) });
    charger();
  };
  const prendre = async (id) => {
    await fetch(`${API_BASE}/api/project/tasks/${id}/claim`, { method: "POST", headers: h });
    await bouger(id, "en_cours");
  };

  // Une action qui retire quelque chose de l'écran demande toujours un
  // deuxième clic : le premier arme, le second exécute. Pas de fenêtre
  // système, qui bloque tout et qu'on clique sans lire.
  const agir = async (url, methode) => {
    try {
      const r = await fetch(`${API_BASE}${url}`, { method: methode, headers: h });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Erreur serveur");
      setAConfirmer(null); setErreur(null); charger();
    } catch (e) { setErreur(e.message); }
  };
  const jeter     = (id) => agir(`/api/bugs/${id}`, "DELETE");
  const restaurer = (id) => agir(`/api/bugs/${id}/restaurer`, "POST");
  const effacer   = (id) => agir(`/api/bugs/${id}/definitif`, "DELETE");

  const droits = (donnees && donnees.droits) || {};
  // Jeter, restaurer, effacer : admins et fondateurs, personne d'autre.
  // Pas même l'auteur du signalement — son rôle est de remonter, pas de
  // trier ce qui mérite d'être gardé.
  const peutJeter = () => !!droits.admin;

  const total = donnees ? donnees.total : 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* En-tête */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <h1 style={{ color: THEME.text, fontSize: 22, fontWeight: 800, margin: 0 }}>🐞 Bugs</h1>
        <span style={{ color: THEME.textMuted, fontSize: 13 }}>
          tu constates, tu remontes, un admin prend le relais
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {/* La corbeille ne se regarde pas non plus quand on n'y a pas droit :
              un bouton qui répond « interdit » n'apprend rien, il agace. */}
          {droits.admin && (
            <button onClick={() => { setCorbeille((v) => !v); setOuvert(false); }}
              style={bouton(corbeille ? THEME.amberDim : "transparent",
                            corbeille ? THEME.amber : THEME.border,
                            corbeille ? THEME.amber : THEME.textMuted)}>
              🗑 Corbeille
              {donnees && donnees.dans_la_corbeille > 0 && !corbeille && " · " + donnees.dans_la_corbeille}
            </button>
          )}
          {!corbeille && (
            <button onClick={() => setOuvert((v) => !v)}
              style={bouton(THEME.redDim, THEME.red, THEME.red)}>
              {ouvert ? "Annuler" : "+ Signaler un bug"}
            </button>
          )}
        </div>
      </div>

      {erreur && (
        <div style={{ background: THEME.redDim, color: THEME.red, border: `1px solid ${THEME.red}`,
                      borderRadius: 8, padding: 11, fontSize: 13 }}>{erreur}</div>
      )}

      {/* Le formulaire */}
      {ouvert && !corbeille && (
        <div style={panneau}>
          <div style={{ ...etiquette, marginBottom: 12 }}>Signaler un bug</div>
          <div style={{ color: THEME.textMuted, fontSize: 12.5, marginBottom: 14, lineHeight: 1.5 }}>
            Écris ce que tu as vu, pas ce que tu crois qui se passe. « Ça marche pas »
            ne se corrige pas — <b>où</b>, <b>quand</b>, et surtout <b>ce qui devait
            arriver</b> à côté de <b>ce qui est arrivé</b>, ça se corrige.
          </div>

          <div style={{ display: "grid", gap: 10,
                        gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)" }}>
            <div style={{ gridColumn: isMobile ? "auto" : "1 / 3" }}>
              <div style={{ ...etiquette, marginBottom: 5 }}>Le bug en une phrase</div>
              <input value={f.titre} onChange={(e) => setF({ ...f, titre: e.target.value })}
                placeholder="ex : la porte de la mine ne se rouvre pas apres le cycle" style={champ} />
            </div>
            <div>
              <div style={{ ...etiquette, marginBottom: 5 }}>Où</div>
              <input value={f.ou} onChange={(e) => setF({ ...f, ou: e.target.value })}
                placeholder="serveur, salle, coordonnees" style={champ} />
            </div>
            <div>
              <div style={{ ...etiquette, marginBottom: 5 }}>Quand</div>
              <input value={f.quand} onChange={(e) => setF({ ...f, quand: e.target.value })}
                placeholder="l'heure, meme approximative" style={champ} />
            </div>
            <div>
              <div style={{ ...etiquette, marginBottom: 5 }}>Ce qui devait se passer</div>
              <input value={f.attendu} onChange={(e) => setF({ ...f, attendu: e.target.value })} style={champ} />
            </div>
            <div>
              <div style={{ ...etiquette, marginBottom: 5 }}>Ce qui s'est passé</div>
              <input value={f.constate} onChange={(e) => setF({ ...f, constate: e.target.value })} style={champ} />
            </div>
            <div>
              <div style={{ ...etiquette, marginBottom: 5 }}>Reproductible ?</div>
              <input value={f.reproductible} onChange={(e) => setF({ ...f, reproductible: e.target.value })}
                placeholder="as-tu reessaye ? a chaque fois ?" style={champ} />
            </div>
            <div>
              <div style={{ ...etiquette, marginBottom: 5 }}>Joueur concerné</div>
              <input value={f.joueur} onChange={(e) => setF({ ...f, joueur: e.target.value })}
                placeholder="son pseudo, s'il y en a un" style={champ} />
            </div>
            <div>
              <div style={{ ...etiquette, marginBottom: 5 }}>Priorité</div>
              <select value={f.priorite} onChange={(e) => setF({ ...f, priorite: e.target.value })} style={champ}>
                <option value="haute">haute — ça bloque le jeu ou ça fait perdre des objets</option>
                <option value="moyenne">moyenne — le reste</option>
                <option value="basse">basse — un détail d'affichage</option>
              </select>
            </div>
          </div>

          <div style={{ marginTop: 14 }}>
            <button onClick={declarer} disabled={envoi}
              style={{ ...bouton(THEME.redDim, THEME.red, THEME.red), opacity: envoi ? 0.6 : 1 }}>
              {envoi ? "Envoi…" : "Envoyer le signalement"}
            </button>
          </div>
        </div>
      )}

      {/* Ce que la page dit d'elle-même */}
      {donnees && corbeille && (
        <div style={{ color: THEME.textMuted, fontSize: 12.5, lineHeight: 1.55 }}>
          {total === 0
            ? "La corbeille est vide."
            : `${total} signalement${total > 1 ? "s" : ""} à la corbeille. Ils ne comptent plus nulle part, mais ils ne sont pas perdus : « Restaurer » les remet à leur place.`}
        </div>
      )}

      {donnees && !corbeille && (
        <div style={{ color: THEME.textMuted, fontSize: 12.5, lineHeight: 1.55 }}>
          {total} bug{total > 1 ? "s" : ""} au total, dont <b>{donnees.du_chat}</b> remonté
          {donnees.du_chat > 1 ? "s" : ""} tout seul{donnees.du_chat > 1 ? "s" : ""} du chat en
          jeu et de Discord par l'analyse du chat.{" "}
          {donnees.du_chat === 0 && "Aucun pour l'instant — ce n'est pas forcément un bon signe : ça peut aussi vouloir dire que la remontée automatique ne tourne plus."}
        </div>
      )}

      {/* Les trois colonnes */}
      {!donnees && !erreur && <div style={{ color: THEME.textMuted }}>Chargement…</div>}

      {donnees && (
        <div style={{ display: "grid", gap: 14,
                      gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", alignItems: "start" }}>
          {COLONNES.map((col) => {
            const liste = donnees.colonnes[col.cle] || [];
            return (
              <div key={col.cle} style={panneau}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 3 }}>
                  <span style={{ color: col.couleur, fontSize: 14, fontWeight: 800 }}>{col.titre}</span>
                  <span style={{ color: THEME.textDim, fontSize: 11 }}>{liste.length}</span>
                </div>
                <div style={{ color: THEME.textMuted, fontSize: 11.5, marginBottom: 12 }}>{col.aide}</div>

                {liste.length === 0 && (
                  <div style={{ color: THEME.textDim, fontSize: 12.5 }}>Rien ici.</div>
                )}

                {liste.map((b) => {
                  const p = PRIO[b.priority] || PRIO.moyenne;
                  const deplie = ouvre === b.id;
                  return (
                    <div key={b.id}
                      style={{ background: THEME.surface, border: `1px solid ${THEME.border}`,
                               borderLeft: `3px solid ${p.couleur}`, borderRadius: 8,
                               padding: "10px 12px", marginBottom: 8 }}>
                      <div onClick={() => setOuvre(deplie ? null : b.id)}
                        style={{ color: THEME.text, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                        {b.title}
                      </div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6, alignItems: "center" }}>
                        <span style={{ ...petit(p.fond, p.couleur, p.couleur), cursor: "default" }}>{p.label}</span>
                        {b.category === "chat_bug" && (
                          <span style={{ ...petit(THEME.purpleDim, THEME.purple, THEME.purple), cursor: "default" }}>
                            💬 du chat
                          </span>
                        )}
                        <span style={{ color: THEME.textDim, fontSize: 11 }}>{jour(b.created_at)}</span>
                        {b.assigne && (
                          <span style={{ color: THEME.accent, fontSize: 11 }}>· {b.assigne}</span>
                        )}
                      </div>

                      {deplie && (
                        <div style={{ color: THEME.textMuted, fontSize: 12.5, marginTop: 9,
                                      whiteSpace: "pre-wrap", lineHeight: 1.55,
                                      borderTop: `1px solid ${THEME.border}`, paddingTop: 9 }}>
                          {b.description || "Aucun détail écrit."}
                          <div style={{ color: THEME.textDim, fontSize: 11, marginTop: 8 }}>
                            projet « {b.projet || "?"} » · signalé par {b.auteur || "—"}
                            {b.supprime_le && ` · jeté le ${jour(b.supprime_le)} par ${b.jete_par || "—"}`}
                          </div>
                        </div>
                      )}

                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 9 }}>
                        {corbeille ? (
                          <>
                            <button onClick={() => restaurer(b.id)}
                              style={petit(THEME.emeraldDim, THEME.emerald, THEME.emerald)}>
                              Restaurer
                            </button>
                            {droits.fondateur && (
                              aConfirmer === b.id
                                ? (
                                  <>
                                    <button onClick={() => effacer(b.id)}
                                      style={petit(THEME.redDim, THEME.red, THEME.red)}>
                                      Oui, effacer pour de bon
                                    </button>
                                    <button onClick={() => setAConfirmer(null)}
                                      style={petit("transparent", THEME.border, THEME.textMuted)}>
                                      Non
                                    </button>
                                  </>
                                )
                                : (
                                  <button onClick={() => setAConfirmer(b.id)}
                                    style={petit("transparent", THEME.red, THEME.red)}>
                                    Effacer définitivement
                                  </button>
                                )
                            )}
                          </>
                        ) : (
                          <>
                            {col.cle === "a_faire" && (
                              <button onClick={() => prendre(b.id)}
                                style={petit(THEME.accentDim, THEME.accent, THEME.accent)}>
                                Je le prends
                              </button>
                            )}
                            {col.cle === "en_cours" && (
                              <>
                                <button onClick={() => bouger(b.id, "termine")}
                                  style={petit(THEME.emeraldDim, THEME.emerald, THEME.emerald)}>
                                  Corrigé
                                </button>
                                <button onClick={() => bouger(b.id, "a_faire")}
                                  style={petit("transparent", THEME.border, THEME.textMuted)}>
                                  Je le repose
                                </button>
                              </>
                            )}
                            {col.cle === "termine" && (
                              <button onClick={() => bouger(b.id, "en_cours")}
                                style={petit("transparent", THEME.border, THEME.textMuted)}>
                                Ça recommence
                              </button>
                            )}
                            {peutJeter() && (
                              aConfirmer === b.id
                                ? (
                                  <>
                                    <button onClick={() => jeter(b.id)}
                                      style={petit(THEME.amberDim, THEME.amber, THEME.amber)}>
                                      Confirmer
                                    </button>
                                    <button onClick={() => setAConfirmer(null)}
                                      style={petit("transparent", THEME.border, THEME.textMuted)}>
                                      Non
                                    </button>
                                  </>
                                )
                                : (
                                  <button onClick={() => setAConfirmer(b.id)}
                                    style={petit("transparent", THEME.border, THEME.textDim)}
                                    title="À la corbeille — réversible">
                                    🗑
                                  </button>
                                )
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ color: THEME.textDim, fontSize: 11, borderTop: `1px solid ${THEME.border}`, paddingTop: 10 }}>
        Cette page lit et écrit dans la même table que <b>Gestion projet</b> : un bug
        déclaré ici apparaît là-bas, et inversement. Rien n'est dupliqué. Un bug
        mis à la corbeille disparaît des deux — mais il n'est pas effacé.
      </div>
    </div>
  );
}
